import React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Ribbon } from '@99designs/frontend-components/components/Ribbon';
import { formatHTMLMessage, formatMessage } from '@99designs/frontend-components/i18n';
import Attribution from '../../../../shared/components/Attribution';
import AvatarPlaceholder from '../../../../shared/components/AvatarPlaceholder';
import AvatarWithAttribution from '../../../../shared/components/AvatarWithAttribution';
import AvatarWithMessage from '../../../../shared/components/AvatarWithMessage';
import Blockquote from '../../../../shared/components/Blockquote';
import Box from '../../../../shared/components/Box';
import Carousel from '../../../../shared/components/Carousel';
import ContentSideFeature from '../../../../shared/components/ContentSideFeature';
import CtaPair from '../../../../shared/components/CtaPair';
import DesignCard from '../../../../shared/components/DesignCard';
import Heading from '../../../../shared/components/Heading';
import { CTALink } from '../../../../shared/components/Link';
import localizedLowercase from '../../../../shared/lib/localizedLowercase';
import {
    avatarPath,
    categoryLaunchPath,
    categoryPath,
    profilePath,
} from '../../../../shared/lib/platformUrls';
import { colors } from '../../../../shared/lib/styles';
import { UserAvatar } from '../../../../shared/spa/core-components/Avatar';
import { ContestType } from '../ContestOverviewData';

export interface Props {
    contest: ContestType;
}

const Message = styled.span`
    em {
        color: ${colors.grey800};
        font-style: normal;
    }
`;

const AvatarWithMessageWrapper = styled.div`
    display: inline-grid;
    max-width: 500px;
`;

function Hero({ contest }: Props) {
    const { locale } = useIntl();
    return (
        <>
            <Box
                paddingBottom="space9"
                textAlign={['left', 'center', 'left', 'left']}
                style={{
                    maxWidth: 1120,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                }}
            >
                <AvatarWithMessageWrapper>
                    <AvatarWithMessage
                        avatar={<AvatarPlaceholder size={['small', 'large', 'large', 'large']} />}
                        role={__('99designs client')}
                        message={
                            <Heading type="h5" variations={['h5', 'soft', 'no-margin']}>
                                <Message
                                    dangerouslySetInnerHTML={{
                                        __html: formatHTMLMessage(
                                            contest.isCompleted
                                                ? __(
                                                      '<em>{customerName}</em> got their new {category} by running a design contest:'
                                                  )
                                                : __(
                                                      '<em>{customerName}</em> is running a design contest to get a new {category}:'
                                                  ),
                                            {
                                                customerName: contest.owner.displayName,
                                                category: localizedLowercase(
                                                    locale,
                                                    contest.category.title
                                                ),
                                            }
                                        ),
                                    }}
                                />
                            </Heading>
                        }
                    />
                </AvatarWithMessageWrapper>
            </Box>

            <ContentSideFeature
                variations={['centered-responsive']}
                side={
                    <div
                        style={{
                            maxWidth: 500,
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            width: '100%',
                            verticalAlign: 'bottom',
                        }}
                    >
                        <Box textAlign="center" paddingRight={['none', 'none', 'none', 'none']}>
                            {contest.isCompleted ? (
                                <Carousel
                                    items={contest.winningEntries.map((winningEntry) => (
                                        <DesignCard
                                            src={winningEntry.designUrl}
                                            alt={formatMessage(
                                                __(
                                                    '"{contestTitle}" winning {contestCategory} by {designerName}'
                                                ),
                                                {
                                                    contestTitle: contest.title,
                                                    contestCategory: contest.category.title,
                                                    designerName:
                                                        winningEntry.designer.user.displayName,
                                                }
                                            )}
                                            ribbon={
                                                <Ribbon variations={['large']}>
                                                    {__('Winner')}
                                                </Ribbon>
                                            }
                                            attribution={
                                                <AvatarWithAttribution
                                                    avatar={
                                                        <UserAvatar
                                                            userId={winningEntry.designer.id}
                                                            size="extra-small"
                                                            userName={
                                                                winningEntry.designer.user
                                                                    .displayName
                                                            }
                                                        />
                                                    }
                                                    attribution={
                                                        <Attribution
                                                            name={
                                                                winningEntry.designer.user
                                                                    .displayName
                                                            }
                                                            size="root"
                                                        />
                                                    }
                                                />
                                            }
                                            width={500}
                                            height={500}
                                            variations={['lazyload', 'heavy-shadow']}
                                        />
                                    ))}
                                    variations={['without-shadow', 'with-attribution']}
                                    animation="fade-in"
                                    autoPlay
                                />
                            ) : (
                                <DesignCard
                                    alt=""
                                    ribbon={
                                        <Ribbon variations={['large', 'info']}>
                                            {__('Open contest')}
                                        </Ribbon>
                                    }
                                    placeholderMessage={__(
                                        'Designers are currently working on this contest…'
                                    )}
                                    width={500}
                                    height={500}
                                    variations={['lazyload', 'heavy-shadow']}
                                />
                            )}
                        </Box>
                    </div>
                }
                top={
                    <Box
                        paddingTop={['none', 'space14', 'none', 'none']}
                        paddingBottom={['space10', 'space10', 'space12', 'space12']}
                        paddingLeft={['none', '20%', 'space14', 'space14']}
                        paddingRight={['none', '20%', 'none', 'none']}
                        textAlign={['left', 'left', 'left', 'left']}
                    >
                        <Blockquote
                            quote={
                                <Heading type="h1" variations={['h1', 'no-margin']}>
                                    {contest.title}
                                </Heading>
                            }
                            variations={['hero']}
                        />
                    </Box>
                }
                bottom={
                    <Box
                        paddingTop={['space10', 'space10', 'none', 'none']}
                        paddingLeft={['none', 'none', 'space14', 'space14']}
                        textAlign={['center', 'center', 'left', 'left']}
                    >
                        <CtaPair
                            variations={['stacked', 'no-margin', 'chameleon']}
                            primary={{
                                label: __('Get your own design'),
                                url: categoryLaunchPath(contest.category.key),
                                color: colors.heritage,
                                variations: ['jumbo', 'primary', 'chameleon', 'responsive'],
                            }}
                            secondary={
                                <CTALink
                                    href={categoryPath(contest.category.key)}
                                    target="_blank"
                                    color="heritage"
                                >
                                    {formatMessage(__('Learn more about {categoryTitle}'), {
                                        categoryTitle: localizedLowercase(
                                            locale,
                                            contest.category.title
                                        ),
                                    })}
                                </CTALink>
                            }
                        />
                    </Box>
                }
            />
        </>
    );
}

export default Hero;
